import { useCallback } from "react"
import type { PartnerAddress } from "@stockline/shared/types/crm/partner-address"
import { PARTNER_ADDRESS_TABLE } from "../../../lib/offline/resources/crm/partner-address/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreatePartnerAddressInput {
  partnerId: string
  name: string
  address1?: string | null
  address2?: string | null
  city?: string | null
  zipCode?: string | null
  code?: string | null
  state?: string | null
  country?: string | null
  isDefault?: boolean
  aiContext?: string | null
}

export interface UpdatePartnerAddressInput {
  name?: string
  address1?: string | null
  address2?: string | null
  city?: string | null
  zipCode?: string | null
  code?: string | null
  state?: string | null
  country?: string | null
  isDefault?: boolean
  aiContext?: string | null
}

export function usePartnerAddressMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createPartnerAddress = useCallback(
    async (input: CreatePartnerAddressInput): Promise<PartnerAddress> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const address: PartnerAddress = {
        id,
        companyId: "",
        partnerId: input.partnerId,
        name: input.name,
        address1: input.address1 ?? null,
        address2: input.address2 ?? null,
        city: input.city ?? null,
        zipCode: input.zipCode ?? null,
        code: input.code ?? null,
        state: input.state ?? null,
        country: input.country ?? null,
        isDefault: input.isDefault ?? false,
        // Read-only role marker — set by data imports, never from the UI.
        isBilling: false,
        aiContext: input.aiContext ?? null,
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(PARTNER_ADDRESS_TABLE, address)
      await changeQueue.enqueue("partner-addresses", {
        op: "create",
        id,
        data: address,
      })
      void engine.pushChanges("partner-addresses")

      return address
    },
    [storage, changeQueue, engine]
  )

  const updatePartnerAddress = useCallback(
    async (id: string, input: UpdatePartnerAddressInput): Promise<void> => {
      const existing = await storage.get<PartnerAddress>(
        PARTNER_ADDRESS_TABLE,
        id
      )
      if (!existing) return

      const updated: PartnerAddress = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(PARTNER_ADDRESS_TABLE, updated)
      await changeQueue.enqueue("partner-addresses", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("partner-addresses")
    },
    [storage, changeQueue, engine]
  )

  const deletePartnerAddress = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(PARTNER_ADDRESS_TABLE, id)
      await changeQueue.enqueue("partner-addresses", {
        op: "delete",
        id,
      })
      void engine.pushChanges("partner-addresses")
    },
    [storage, changeQueue, engine]
  )

  return { createPartnerAddress, updatePartnerAddress, deletePartnerAddress }
}
